import { useCallback, useEffect, useMemo, useRef } from 'react';
import { isEqual } from 'lodash';
import { RiAddFill as AddIcon } from 'react-icons/ri';
import styles from './DebtList.module.scss';
import MeritoButton from '../common/MeritoButtons/MeritoButtons';
import { Debt, DebtCardData } from '../../interfaces/interfaces';
import DebtCard from './DebtCard';
import { getDefaultDebt, isDraftDebt } from './debtHelpers';
import { createDebtApi } from './debtApi';
import { useDebtSocket } from './useDebtSocket';
import { hasNoUploadedDebt } from './debtValidation';

type Props = {
    debts: DebtCardData[];
    // takes an updater like a useState setter, so a late upload or socket
    // result applies to the latest list rather than the one it started from
    onDebtsChange: (update: (debts: DebtCardData[]) => DebtCardData[]) => void;
    // where this flow's debt routes live, e.g. "/underwriting/debt"
    apiBasePath: string;
    // the owning form was submitted with errors; cards then show missing details
    showErrors?: boolean;
};

// A card's identity: drafts carry a clientId, server debts always have a
// debtId. Used as the React key and to find a card when an update lands, never
// its position, which shifts whenever a card above it is removed.
const cardKey = (debt: DebtCardData): string =>
    isDraftDebt(debt) ? debt.clientId : debt.clientId ?? debt.debtId;

// The debt cards on their own, with no form library behind them, so any flow
// (underwriting, pulse) can hold the list in whatever state it uses.
const DebtList = ({ debts, onDebtsChange, apiBasePath, showErrors = false }: Props) => {
    const api = useMemo(() => createDebtApi(apiBasePath), [apiBasePath]);
    const addedDebtKeyRef = useRef<string | null>(null);
    const addedDebtElementRef = useRef<HTMLDivElement>(null);

    // The parent passes a new onDebtsChange on every render. Listing it as an
    // effect dependency would re-run the effects (and re-fetch) on every render,
    // so keep the latest one in a ref and hand out one stable updater instead,
    // the same pattern useDebtSocket uses for its handler.
    const onDebtsChangeRef = useRef(onDebtsChange);
    onDebtsChangeRef.current = onDebtsChange;
    const updateDebts = useCallback(
        (update: (debts: DebtCardData[]) => DebtCardData[]) => onDebtsChangeRef.current(update),
        []
    );

    // Looks the card up when the update runs, so a late upload reply still finds
    // its own card after others moved. Writes the key back as clientId so it
    // survives content swaps (draft to real debt, real debt back to draft) and
    // React never remounts the card mid-flow.
    const replaceDebt = (key: string, debt: DebtCardData) =>
        updateDebts((current) =>
            current.map((item) => (cardKey(item) === key ? { ...debt, clientId: key } : item))
        );

    const removeDebt = (key: string) =>
        updateDebts((current) => current.filter((item) => cardKey(item) !== key));

    // Catch up with the server: results that finished while this list wasn't
    // listening (another step, or a dropped socket) are only in the database.
    // Swaps in the server's copy of each debt by debtId, and only when it
    // differs, so a visit with nothing new doesn't mark the form as changed.
    const syncWithServer = useCallback(async () => {
        let serverDebts: Debt[];
        try {
            serverDebts = await api.getDebts();
        } catch {
            return; // already logged; the cards keep what they have
        }
        // the list endpoint adds a job summary the cards never carry
        const serverById = new Map(
            serverDebts.map(({ job, ...debt }: Debt & { job?: unknown }) => [debt.debtId, debt as Debt])
        );
        updateDebts((current) => {
            let changed = false;
            const next = current.map((item) => {
                const serverDebt = item?.debtId ? serverById.get(item.debtId) : undefined;
                // the server copy never has clientId, so leave it out of the comparison
                const { clientId, ...itemData } = item;
                // isEqual, not JSON.stringify: MySQL reorders JSON keys, so the listed
                // copy and a socket copy can be equal yet serialise differently
                if (!serverDebt || isEqual(serverDebt, itemData)) return item;
                changed = true;
                return { ...serverDebt, clientId };
            });
            return changed ? next : current;
        });
    }, [api, updateDebts]);

    // same two triggers as the bank statements module: on mount here, and on
    // socket reconnect below
    useEffect(() => {
        syncWithServer();
    }, [syncWithServer]);

    useEffect(() => {
        if (!addedDebtKeyRef.current || !addedDebtElementRef.current) return;
        addedDebtElementRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        addedDebtKeyRef.current = null;
    }, [debts]);

    // OCR results arrive over the socket; swap the matching card for the server's copy
    useDebtSocket(
        (updatedDebt) => {
            updateDebts((current) => {
                if (!current.some((item) => item?.debtId === updatedDebt.debtId)) return current;
                // keep the card's clientId so its key doesn't change under it
                return current.map((item) =>
                    item?.debtId === updatedDebt.debtId ? { ...updatedDebt, clientId: item.clientId } : item
                );
            });
        },
        syncWithServer
    );

    // Next failed and nothing is uploaded yet: the blank card asks for its letter
    const showUploadError = showErrors && hasNoUploadedDebt(debts);

    return (
        <div className={styles.debtsContainer}>
            {debts.map((debt) => {
                const key = cardKey(debt);
                return (
                    <DebtCard
                        // the card's own id, not its position: local state (touched
                        // fields, pending upload) stays with the card it belongs to
                        key={key}
                        containerRef={key === addedDebtKeyRef.current ? addedDebtElementRef : undefined}
                        debt={debt}
                        onChange={(updatedDebt) => replaceDebt(key, updatedDebt)}
                        onRemove={() => removeDebt(key)}
                        isOnlyCard={debts.length === 1}
                        api={api}
                        showErrors={showErrors}
                        // only blank cards can show it; with nothing uploaded, every card is blank
                        showUploadError={showUploadError && !debt?.debtId}
                    />
                );
            })}
            <div className={styles.addBtnActionContainer}>
                <MeritoButton
                    btnText="Add Debt"
                    type='secondary-1'
                    size='md'
                    icon={<AddIcon />}
                    onClick={() => {
                        const debt = getDefaultDebt();
                        addedDebtKeyRef.current = debt.clientId;
                        updateDebts((current) => [...current, debt]);
                    }}
                />
            </div>
        </div>
    );
};

export default DebtList;
